import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@my-saas/ui";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { useCreateTenant, useMyTenants } from "@/client/services/onboarding";

/**
 * Self-serve tenant creation. Lands here after sign-up; the user picks a
 * display name and (optionally) a URL slug. Backend creates Tenant{status:
 * ONBOARDING} + Membership{role:TENANT_SUPERADMIN} in one transaction.
 */
export const CreateTenantPage = () => {
  const navigate = useNavigate();
  const create = useCreateTenant();
  const { tenants } = useMyTenants();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const tenant = await create.mutateAsync({
        name: name.trim(),
        slug: slug.trim() || undefined,
      });
      toast({ title: "Tenant created", description: `${tenant.name} (${tenant.slug})` });
      void navigate(`/dashboard/t/${tenant.slug}`);
    } catch (e) {
      toast({
        variant: "error",
        title: "Could not create tenant",
        description: (e as Error).message,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Create tenant</title>
      </Helmet>

      <div className="mx-auto max-w-xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Create your tenant</h1>
          <p className="text-sm text-muted-foreground">
            A tenant is your organization's workspace — OREF subscriptions,
            WhatsApp groups, and members all live inside a tenant. You can
            belong to more than one.
          </p>
        </div>

        {tenants.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">You already belong to:</CardTitle>
              <CardDescription>Jump straight in or create a new one below.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              {tenants.map((t) => (
                <Link
                  key={t.tenant.id}
                  to={`/dashboard/t/${t.tenant.slug}`}
                  className="flex items-center justify-between rounded border px-3 py-2 text-sm hover:bg-muted"
                >
                  <span>
                    <span className="font-medium">{t.tenant.name}</span>{" "}
                    <span className="text-xs text-muted-foreground">({t.tenant.slug})</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{t.role}</span>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>New tenant</CardTitle>
            <CardDescription>You become the TENANT_SUPERADMIN.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization name</Label>
                <Input
                  id="name"
                  required
                  placeholder="e.g. Kafri Emergency"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL slug (optional)</Label>
                <Input
                  id="slug"
                  placeholder="auto-generated if blank"
                  value={slug}
                  onChange={(e) =>
                    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))
                  }
                  pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                />
                <p className="text-xs text-muted-foreground">
                  Used in your dashboard URL: /dashboard/t/{slug || "your-slug"}
                </p>
              </div>
              <Button type="submit" disabled={create.isPending || !name.trim()} className="w-full">
                {create.isPending ? "Creating…" : "Create tenant"}
                <ArrowRightIcon className="ml-2 size-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
